import type { PricingList } from 'api/types/pricing.types';
import Restoration from 'assets/images/instruction_images/restoration.jpg';
import SoleIcing from 'assets/images/instruction_images/sole_icing.jpg';
import StainProtection from 'assets/images/instruction_images/stain-protection.jpg';
import useImagePreloader from 'hooks/useImagePreloader';
import i18n from 'i18n/config';
import { usePricing } from 'pages/queries/pricing.queries';
import React, { useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { Trans } from 'react-i18next';
import { styled } from 'styled-components';
import { getShoeAddOnPricing } from 'utils/helpers';

import type { IPreferencesOptions } from '../OrderInstructionsOptions';
import PriceView from './PriceView';

const StyledImage = styled.img`
  width: 100%;
  object-fit: cover;
  border-radius: 8px;
  height: 168px;
  flex-shrink: 0;
`;

const StyledDiv = styled.div`
  padding-right: 6px;
  padding-left: 6px;
`;

const StyledTitle = styled.h5`
  color: ${({ theme }) => theme.components.orderInstructions.bottomSheetHeading.textColor};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  margin-top: 10px;
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.steelBlue};
  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 300;
  line-height: 18px;
  letter-spacing: 0.1px;
`;

const StyledUL = styled.ul`
  padding-left: 18px;
`;

const StyledDescription = styled.p`
  color: ${({ theme }) => theme.colors.steelBlue};
  font-size: 12px;
  font-style: normal;
  font-weight: 300;
  line-height: 18px;
  letter-spacing: 0.1px;
  margin-bottom: 6px;
`;

const StyledBoldText = styled.span`
  font-weight: 500;
`;

const Saperator = styled.div`
  height: 6px;
  background: ${({ theme }) => theme.colors.white100};
  margin-top: -4px;
`;

type AddOnType = 'RESTORATION' | 'SOLE_ICING' | 'STAIN_PROTECTION';

type PreferencesContent = {
  [x in keyof IPreferencesOptions]?: {
    banner: string;
    title: string;
    description: string | string[];
    addonType: AddOnType;
  };
};

const contents: PreferencesContent = {
  restoration: {
    banner: Restoration,
    title: i18n.t('newOrderPage.instructionOptions.restoration.heading'),
    description: [
      i18n.t('newOrderPage.instructionOptions.restoration.description.0'),
      i18n.t('newOrderPage.instructionOptions.restoration.description.1'),
      i18n.t('newOrderPage.instructionOptions.restoration.description.2'),
      i18n.t('newOrderPage.instructionOptions.restoration.description.3'),
    ],
    addonType: 'RESTORATION',
  },
  soleIcing: {
    banner: SoleIcing,
    title: i18n.t('newOrderPage.instructionOptions.soleIcing.heading'),
    description: i18n.t('newOrderPage.instructionOptions.soleIcing.description.0'),
    addonType: 'SOLE_ICING',
  },
  stainProtection: {
    banner: StainProtection,
    title: i18n.t('newOrderPage.instructionOptions.stainProtection.heading'),
    description: i18n.t('newOrderPage.instructionOptions.stainProtection.description.0'),
    addonType: 'STAIN_PROTECTION',
  },
};

const PreferenceContent = ({ preferenceType }: { preferenceType: keyof IPreferencesOptions }) => {
  const content = contents[preferenceType];

  const { data } = usePricing();

  useImagePreloader([Restoration, SoleIcing, StainProtection]);

  const price = data && content ? getShoeAddOnPricing(data.services.SHOE_CLEANING as PricingList.PerItemService, content?.addonType as AddOnType) : 0;

  const PreferenceDescription = useMemo(
    () =>
      typeof content?.description === 'string' ? (
        <Description>{content.description}</Description>
      ) : (
        <StyledUL>
          {content?.description?.map((d: string) => (
            <li key={d}>
              <StyledDescription>
                <Trans
                  components={{
                    bold: <StyledBoldText />,
                  }}
                >
                  {d}
                </Trans>
              </StyledDescription>
            </li>
          ))}
        </StyledUL>
      ),
    [content?.description]
  );

  if (!content) return null;

  return (
    <>
      <Container>
        <StyledImage src={content.banner} alt="preference-banner" />
        <StyledDiv>
          <StyledTitle>{content.title}</StyledTitle>
          {PreferenceDescription}
        </StyledDiv>
        {price !== 0 && <PriceView currency={data?.currency} price={price} />}
      </Container>
      <Saperator />
    </>
  );
};

export default PreferenceContent;
